<template>
    <div id="asset-container">
        <div ref="canvasContainer" id="canvas-container"></div>
        <router-link id="back-button" to="/" v-if="$t('general.back').length">{{
            $t("general.back")
        }}</router-link>
        <div id="close-button" v-if="isPlayingAsset" v-on:click="stopPlaying">
            <span class="icon-clear"></span>
        </div>
        <a
            v-if="!contentReported && isPlayingAsset"
            class="report-btn"
            @click.prevent="openReportPopin"
        >
            {{ $t("report.report") }}
        </a>
        <RoundButton
            class="asset-loader"
            v-if="isLoadingAsset && assetDisplayed && $t('general.loading').length"
            background="var(--secondary)"
            textColor="var(--buttonTextColor)"
        >
            {{ $t("general.loading") }}
        </RoundButton>
        <transition name="bounce" appear>
            <div class="record-button-container" v-if="isPlayingAsset && videoPlayed">
                <RoundButton
                    height="calc(var(--vh) * 6)"
                    background="var(--secondary)"
                    textColor="var(--buttonTextColor)"
                    v-if="noSavedExperience || canEditExperience"
                    class="record-link"
                    @click="goToRecord"
                >
                    <div class="red-dot"></div>
                    {{ canEditExperience ? $t("home.edit") : $t("home.record") }}
                </RoundButton>
                <ShareButton
                    v-if="!noSavedExperience && !contentReported"
                    class="share-btn"
                    @click="share"
                />
            </div>
        </transition>
        <transition name="fade" appear>
            <ARPopin class="intro-popin" :text="popinText" v-if="popinText !== ''" />
        </transition>
        <Message
            class="message-popin"
            v-if="isMessage"
            :isError="messageOverlay.isError"
            :info="messageOverlay.info"
            :title="messageOverlay.title"
            :text="messageOverlay.text"
            :isCompliant="messageOverlay.isCompliant"
            :allowCopy="messageOverlay.allowCopy"
            @close="closeMessage"
        />
        <ReportContentPopin
            v-if="displayReportContentPopin"
            @close="closeReportPopin"
            :qrcodeObject="qrcodeObject"
            :videoUrl="videoUrl"
        />
    </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { nativeShare, canNativeShare } from "@/utils";
import { getPit } from "@/argo_ar/ArgoUtils";
import noop from "lodash.noop";

import GyroARExperience from "@/ar/GyroARExperience";
import RoundButton from "@/components/buttons/RoundButton.vue";
import ShareButton from "@/components/buttons/ShareButton.vue";
import ARPopin from "@/components/ARPopin.vue";
import colors from "@/styles/colors";
import Message from "@/components/Message.vue";
import AbstractARExperience from "@/ar/AbstractARExperience";
import { State } from "@/store";
import { mapState } from "vuex";

import ReportContentPopin from "@/components/ReportContentPopin.vue";

window.isAudioActivated = false;

export default defineComponent({
    name: "AR",
    components: {
        RoundButton,
        ARPopin,
        ShareButton,
        Message,
        ReportContentPopin
    },
    data() {
        return {
            pageId: -1,
            arReady: false,
            popinText: "",
            popinTimer: null as TimerHandler | null,
            isLoadingAsset: false,
            isPlayingAsset: false,
            assetPlayed: false,
            videoPlayed: false,
            arExperience: null as AbstractARExperience | null,
            canvas: HTMLCanvasElement,
            isRunning: false,
            assetDisplayed: false,
            colors,
            messageOverlay: {},
            isMessage: false,
            displayReportContentPopin: false
        };
    },
    computed: {
        contentBanned() {
            return this.qrcodeObject.xpBanned || this.qrcodeObject.projectBanned;
        },
        contentReported() {
            return this.qrcodeObject.xpReported || this.qrcodeObject.projectReported;
        },
        noSavedExperience() {
            return !this.qrcodeObject || this.qrcodeObject.isOpen;
        },
        canEditExperience() {
            return (
                this.authenticated &&
                !this.noSavedExperience &&
                !this.contentReported &&
                this.qrcodeObject.experiences.length &&
                this.qrcodeObject.experiences[0].ownerId === this.userId
            );
        },
        ...mapState({
            qrcodeObject: (state: State) => state.qrcode.qrcodeObject,
            videoUrl: (state: State) => state.qrcode.videoUrl,
            userId: (state: State) => state.auth.userId,
            authenticated: (state: State) => state.auth.authenticated
        })
    },
    mounted() {
        this.isRunning = true;
        if (!this.qrcodeObject || !this.qrcodeObject.arEnabled || this.contentBanned) {
            this.$router.replace("/");
            return;
        }

        this.pageId = this.qrcodeObject.pageId;
        this.startPopinTimer();
        this.startXRSession();
    },
    beforeUnmount() {
        this.isRunning = false;
        if (this.videoAssetHTML) {
            this.videoAssetHTML.pause();
        }
        if (this.popinTimer) {
            clearTimeout(this.popinTimer);
        }
        this.stopXRSession();
    },
    unmounted() {
        const arCanvas = document.getElementById("ar-canvas");
        if (arCanvas) {
            arCanvas.remove();
        }
    },
    methods: {
        startPopinTimer() {
            const displayPopin = (localeKey, duration): Promise<void> => {
                return new Promise(resolve => {
                    if (this.$t(localeKey).length) {
                        this.popinText = this.$t(localeKey);
                        this.popinTimer = setTimeout(resolve, duration);
                    } else {
                        resolve();
                    }
                });
            };
            const hidePopin = (duration = 0): Promise<void> => {
                return new Promise(resolve => {
                    this.popinText = "";
                    if (duration > 0) {
                        this.popinTimer = setTimeout(resolve, duration);
                    } else {
                        resolve();
                    }
                });
            };

            hidePopin(1500)
                .then(() => displayPopin("ar.firstPopin", 3000))
                .then(() => {
                    // no need to hide popin for 2s if first popin not displayed
                    const duration = this.$t("ar.firstPopin").length > 0 ? 2000 : 0;
                    return hidePopin(duration);
                })
                .then(() => displayPopin("ar.secondPopin", 4000))
                .then(() => hidePopin());
        },
        /**
         * Dynamically create the canvas for XR session
         * Then initialize the XR session
         */
        async startXRSession() {
            console.log("startXRSession");
            const arCanvas = document.getElementById("ar-canvas");
            if (arCanvas) {
                arCanvas.remove();
            }

            const canvas = document.createElement("canvas");
            canvas.id = "ar-canvas";
            this.canvas = canvas;
            (this.$refs.canvasContainer as HTMLElement).appendChild(canvas);

            this.arExperience = new GyroARExperience(
                canvas,
                this.pageId,
                this.deviceId,
                this.onAssetPlayStart,
                this.onAssetPlayStop,
                this.onVideoPlayStart,
                this.onAssetLoadStart,
                this.onAssetLoadFinished,
                this.onAssetDisplayed,
                this.onAssetHidden,
                this.onARError,
                this.$refs.canvasContainer
            );

            this.arExperience.initialize();

            await this.arExperience.startPlacegroundSession();
            this.arReady = true;
            canvas.classList.add("visible");
        },

        /**
         * Stop the current XR session
         * Then destroy the canvas
         */
        stopXRSession() {
            if (this.canvas.classList) {
                this.canvas.classList.remove("visible");
            }
            if (this.arExperience) {
                this.arExperience.stopPlacegroundSession();
            }
            while (getPit().childNodes.length > 0) {
                getPit().removeChild(getPit().childNodes[0]);
            }
        },

        onAssetPlayStart() {
            this.assetPlayed = true;
            if (this.popinTimer) {
                clearTimeout(this.popinTimer);
                this.popinText = "";
            }
            this.isPlayingAsset = true;
        },

        onAssetLoadStart() {
            this.isLoadingAsset = true;
        },

        onAssetLoadFinished() {
            this.isLoadingAsset = false;
        },

        onAssetDisplayed() {
            this.assetDisplayed = true;
        },

        onAssetHidden() {
            this.assetDisplayed = false;
        },

        onAssetPlayStop() {
            this.isPlayingAsset = false;
        },

        onVideoPlayStart() {
            this.videoPlayed = true;
        },

        onARError(error: { message: string }) {
            this.showMessage(
                true,
                this.$t("errors.title"),
                this.$t(error.message),
                true,
                false,
                () => {
                    this.$router.replace("/");
                }
            );
        },

        stopPlaying() {
            if (this.isPlayingAsset) {
                this.arExperience?.stopPlayingAsset();
            }
        },

        goToRecord() {
            this.$router.push("Record");
        },

        share() {
            const shareTitle = this.noSavedExperience
                ? this.$t("share.openTitle")
                : this.$t("share.burnedTitle");
            // experience name
            const shareText = this.noSavedExperience
                ? this.$t("share.openText")
                : this.qrcodeObject.experiences[0].name;

            if (canNativeShare()) {
                nativeShare(shareTitle, shareText, document.location.href.split("#")[0]);
            } else {
                this.showMessage(
                    false,
                    this.$t("general.share"),
                    `${shareText} ${document.location.href.split("#")[0]}`,
                    true,
                    true
                );
            }
        },

        showMessage(_isError, title, text, isCompatible = true, allowCopy = false, onClose = noop) {
            this.messageOverlay = {
                isError: _isError,
                title: title,
                text: text,
                isCompliant: isCompatible,
                allowCopy,
                onClose
            };
            if (_isError) {
                console.error(text);
            }

            this.isMessage = true;
        },
        closeMessage() {
            this.isMessage = false;

            if (this.messageOverlay.onClose) {
                this.messageOverlay.onClose();
            }

            this.messageOverlay = {};
        },
        openReportPopin() {
            console.log("open report popin", this.videoUrl);
            this.displayReportContentPopin = true;
        },

        closeReportPopin() {
            this.displayReportContentPopin = false;
        }
    }
});
</script>
<style scoped>
.record-button-container {
    position: absolute;
    bottom: 10%;
    left: 0;
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: center;
    z-index: 3;
}

.record-link {
    margin-right: 5px;
    font-weight: bold;
}

.share-btn {
    margin-left: 5px;
}

.asset-loader {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) scale(1.5);
    z-index: 3;
}

#back-button {
    position: absolute;
    top: 20px;
    left: 20px;
    font-weight: bold;
    text-decoration: none;
    color: var(--white);
    z-index: 3;
}

.report-btn {
    position: absolute;
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
    font-weight: bold;
    color: var(--white);
    text-decoration: none;
    z-index: 9999;
}

#close-button {
    position: absolute;
    top: 20px;
    right: 20px;
    font-size: 2em;
    font-weight: bold;
    text-decoration: none;
    color: var(--white);
    z-index: 3;
}

#canvas-container {
    height: 100%;
    width: 100%;
}
</style>
<style>
#ar-canvas {
    transition: opacity 1s linear;
    opacity: 0;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 2;
}

#ar-canvas.visible {
    opacity: 1;
}

.intro-popin {
    position: absolute;
    top: 10%;
    left: 10%;
    right: 10%;
    z-index: 3;
}

.red-dot {
    height: 2.5vh;
    width: 2.5vh;
    background: var(--red);
    border-radius: 50%;
    margin-right: 10px;
}
</style>
